import { createSingleton } from 'tippy.js'
import { base as baseTippyProps } from './tippy-props'

/**
 * This is the key used to store the singleton instance of this registry on the
 * root document node. We use a "Symbol" here to better conceal this property
 * and prevent it from being visible and accessed.
 * @type {Symbol}
 */
const tooltipRegistryKey = Symbol('tooltip registry')

/**
 * Helper class to be used as a singleton instance to keep records of every
 * tooltip element that's been initialized into a Tippy instance. We refer to
 * this collection in order to create a singleton Tippy instance for all
 * tooltips that are grouped by a common string value. Tooltips belonging to
 * the same group are added to the same Tippy singleton for better UX and
 * performance.
 */
export default class TooltipRegistry {
  static getInstance () {
    if (!document[tooltipRegistryKey]) {
      document[tooltipRegistryKey] = new TooltipRegistry()
    }
    return document[tooltipRegistryKey]
  }

  groups = {}

  getGroup (name) {
    if (!this.groups[name]) {
      this.groups[name] = {
        set: new Set(),
        singleton: null
      }
    }
    return this.groups[name]
  }

  register (element) {
    const groupName = element.group
    const group = this.getGroup(groupName)
    group.set.add(element)

    // If tooltip element is grouped, update the group singleton instance.
    if (groupName) {
      this.updateSingleton(groupName)
    }
  }

  unregister (element) {
    const groupName = element.group
    const group = this.getGroup(groupName)
    group.set.delete(element)

    // Update singleton instances with updated group set.
    if (groupName) {
      this.updateSingleton(groupName)
    }

    // When no more items exist in the group, destroy the singleton.
    if (group.set.size === 0) {
      group.singleton?.destroy()
      delete this.groups[groupName]
    }
  }

  /**
   * Iterates through all tooltips and destroys its tippy instance.
   */
  reset () {
    Object.values(this.groups).forEach(group => {
      [...group.set].forEach(element => {
        element.tippy.destroy()
        element.tippy && this.unregister(element)
      })
    })
  }

  updateSingleton (name) {
    const group = this.getGroup(name)
    // filter out elements with undefined tippy instances
    const tippies = [...group.set].filter(element => element.tippy).map(element => element.tippy)

    if (group.singleton) {
      group.singleton.setInstances(tippies)
    } else {
      group.singleton = createSingleton(tippies, {
        overrides: Object.keys(baseTippyProps)
      })
    }
  }
}
